import React, { memo, useCallback, useMemo, useState, useRef } from 'react'
import { makeStyles } from '@material-ui/styles'
import { useTranslation } from 'react-i18next'
import Grid from '@material-ui/core/Grid'
import Hidden from '@material-ui/core/Hidden'
import Fab from '@material-ui/core/Fab'
import Divider from '@material-ui/core/Divider'
import Chip from '@material-ui/core/Chip'
import { useParams, useHistory, useLocation } from 'react-router-dom'
import CircularProgress from '@material-ui/core/CircularProgress'
import { toast } from 'react-toastify'
import { Helmet } from 'react-helmet'
import ReactPlayer from 'react-player'
import ReactGA from 'react-ga'

import { EEntity, EJobTextCategory, ECompanyActionTypes } from 'models'
import { Badge, Loading } from 'components'
import { DialogJobApplicants, CardDescription } from './components'
import { JobEditor } from '../jobCreation/components'
import { StoreValue } from 'store'
import styles from '../styles'
import { buildJobImage, truncateText } from 'utils'
import {
	useApplyJobMutation,
	useInsertIntoJobCollectionMutation,
	useJobQueryQuery,
	useUpdateJobCollectionMutation,
} from '../../../graphql/__generated__/graphql'

const useStyles = makeStyles(styles)

const JobProfileView: React.FunctionComponent<any> = ({
	isCreating,
	handleEditJobDetails,
	jobDetails,
	storeUserProfile,
	storeDispatch,
}) => {
	const {
		pathname,
		state,
	}: {
		pathname: string
		state: { update?: boolean; add?: boolean }
	} = useLocation()

	const [openDialogUploadCvInfo, setOpenDialogUploadCvInfo] = useState(false)
	const classes = useStyles()
	const { t, i18n } = useTranslation()
	const [checkUserJobApplication, setCheckUserJobApplication] =
		useState(false)
	let showLocations = useRef('')
	const { jobSlug }: { companySlug?: string; jobSlug?: string } = useParams()
	const history = useHistory()
	const [applyJobMutation] = useApplyJobMutation()
	const [postJobMutation, { loading: postJobLoading }] =
		useInsertIntoJobCollectionMutation()
	const [editJobMutation] = useUpdateJobCollectionMutation()

	// TODO: check if we need jobData. This query was need for preview page, but we don't need it anymore because anytime we preview the job (on add / update) we already have all the data in jobDetails
	const { data: jobDatas, loading } = useJobQueryQuery({
		skip: isCreating,
		variables: {
			filter: {
				slug: { ilike: jobSlug },
				// companySlug,
			},
		},
	})
	const jobData = jobDatas?.jobCollection?.edges?.map(x => x.node)
	const handleOpenApplyJobDialog = useCallback(() => {
		setOpenDialogUploadCvInfo(!openDialogUploadCvInfo)
	}, [openDialogUploadCvInfo])

	const handleLogIn = useCallback(() => {
		setOpenDialogUploadCvInfo(!openDialogUploadCvInfo)
		sessionStorage.setItem('lastPageVisited', pathname)
		history.push({
			pathname: `/${i18n.language}/auth/login`,
		})
	}, [history, pathname, i18n.language, openDialogUploadCvInfo])

	const getNrHours = useCallback(
		contractType => {
			switch (contractType) {
				case 'FULL_TIME':
					return t('40/week')
				case 'PART_TIME':
					return t('20/week')
				case 'PROJECT_BASED':
					return 'Project Based'
				case 'ONE_TIME':
					return 'One Time'
			}
		},
		[t],
	)

	const getSkills = useCallback(skills => {
		// skills from job reducer are stored in an array with different keys
		const resultSkills = skills?.map((item: any) => {
			return {
				id: item.value,
				title: item.label,
			}
		})
		return resultSkills
	}, [])

	const getLocations = useCallback((isCreating, locations) => {
		if (isCreating) {
			return locations?.map((item: any) => {
				return {
					id: item.value,
					name: item.label,
				}
			})
		} else {
			return locations?.map((item: any) => {
				return {
					id: item.id,
					name: item.location.name,
				}
			})
		}
	}, [])

	const getContractType = useCallback(value => {
		switch (value) {
			case 'FULL_TIME':
				return 'Full Time'
			case 'PART_TIME':
				return 'Part Time'
			case 'PROJECT_BASE':
				return 'Project Based'
			case 'ONE_TIME':
				return 'One Time'
		}
	}, [])

	const getSeniorityLevel = useCallback(value => {
		switch (value) {
			case 'BEGINNER':
				return 'Beginner'
			case 'MIDDLE':
				return 'Middle'
			case 'SENIOR':
				return 'Senior'
		}
	}, [])

	const getJobTypeLocData = useCallback(value => {
		switch (value) {
			case 'ON_SITE':
				return 'OnSite'
			case 'REMOTE':
				return 'Remote'
			case 'ALL':
				return 'OnSite & Remote'
		}
	}, [])

	const getJobTypeLoc = useCallback(value => {
		if (value.length === 2) {
			return 'OnSite & Remote'
		} else if (value.length === 1) {
			const [firstElement] = value
			switch (firstElement) {
				case 'REMOTE':
					return 'Remote'
				case 'ON_SITE':
					return 'OnSite'
			}
		}
	}, [])
	const getJobType = useCallback(value => {
		switch (value) {
			case 'INTERNSHIP':
				return 'Internship'
			case 'INTERN':
				return 'Intern'
			case 'FREELANCE':
				return 'Freelance'
		}
	}, [])

	const jobTemp: any = useMemo(() => {
		const result = {
			id:
				isCreating && jobDetails
					? jobDetails.job.id
					: jobData && jobData.length
					? jobData[0].id
					: '',
			company:
				jobData && jobData.length
					? jobData[0].company
					: jobDetails
					? jobDetails.company
					: {},
			contractType:
				isCreating && jobDetails && jobDetails.job.contractType
					? getContractType(jobDetails.job.contractType)
					: jobData && jobData.length && jobData[0].contractType
					? getContractType(jobData[0].contractType)
					: '',
			nrHours:
				isCreating && jobDetails
					? getNrHours(jobDetails.job.contractType)
					: jobData && jobData.length
					? getNrHours(jobData[0].contractType)
					: '',
			description:
				isCreating && jobDetails
					? jobDetails.job.jobDescription
					: jobData && jobData.length
					? jobData[0].description
					: '',
			skills:
				isCreating && jobDetails
					? getSkills(jobDetails.job.skills)
					: jobData && jobData.length
					? jobData[0]._JobToCustomSkillsCollection?.edges
					: [],

			benefits:
				isCreating && jobDetails
					? jobDetails.job.benefits
					: jobData && jobData.length
					? jobData[0].benefits
					: '',
			locations:
				isCreating && jobDetails && jobDetails.job.locations
					? getLocations(isCreating, jobDetails.job.locations)
					: jobData &&
					  jobData[0]?._JobToCompanyOfficesCollection?.edges.filter(
							x => x.node.A === jobData[0].id,
					  ).length
					? getLocations(
							false,
							jobData[0]?._JobToCompanyOfficesCollection?.edges.filter(
								x => x.node.A === jobData[0].id,
							)[0].node.companyOffices.roLocations,
					  )
					: [],
			seniority:
				isCreating && jobDetails && jobDetails.job.seniorityLevel
					? getSeniorityLevel(jobDetails.job.seniorityLevel)
					: jobData && jobData.length && jobData[0].seniorityLevel
					? getSeniorityLevel(jobData[0].seniorityLevel)
					: '',
			jobTypeLoc:
				isCreating && jobDetails && jobDetails.job.jobTypeLoc
					? getJobTypeLoc(jobDetails.job.jobTypeLoc)
					: jobData && jobData[0].locationType
					? getJobTypeLocData(jobData[0].locationType)
					: '',
			jobType:
				isCreating && jobDetails && jobDetails.job.jobTypeLoc
					? getJobType(jobDetails.job.jobType)
					: jobData && jobData.length && jobData[0].jobType
					? getJobType(jobData[0].jobType)
					: '',
			salary:
				isCreating && jobDetails
					? jobDetails.job.salary
					: jobData && jobData.length
					? jobData[0].jobSalary
					: '',
			team:
				isCreating && jobDetails && jobDetails.team
					? jobDetails.job.team.team
					: jobData &&
					  jobData?.length &&
					  jobData[0]?._TeamToJobCollection?.edges?.length
					? jobData[0]._TeamToJobCollection?.edges?.map(
							x => x.node,
					  )[0] // TODO: this is temporary solution
					: '',
			title:
				isCreating && jobDetails
					? jobDetails.job.title
					: jobData && jobData.length
					? jobData[0].title
					: '',

			expiryDate:
				isCreating && jobDetails
					? jobDetails.job.expiryDate
					: jobData && jobData.length
					? jobData[0].expiryDate
					: '',

			checkLoggedUserApplication:
				jobData && jobData.length
					? jobData[0].jobApplicantCollection?.edges[0].node.id
					: false,
			featuredImage:
				isCreating && jobDetails && jobDetails.job.featuredImage
					? jobDetails.job.featuredImage.filename
					: jobData && jobData.length && jobData[0].featuredImage
					? jobData[0].featuredImage
					: '',
			featuredVideo:
				isCreating && jobDetails && jobDetails.job.featuredVideo
					? jobDetails.job.featuredVideo
					: jobData && jobData.length && jobData[0].videoUrl
					? jobData[0].videoUrl
					: '',
			applyJobExternalUrl:
				isCreating && jobDetails?.job?.applyJobExternalUrl
					? jobDetails.job.applyJobExternalUrl
					: jobData && jobData[0]?.applyJobExternalUrl
					? jobData[0].applyJobExternalUrl
					: '',
		}

		if (result && result.checkLoggedUserApplication) {
			setCheckUserJobApplication(true)
		}

		if (result && result.locations) {
			let parseLocations = result?.locations?.map((item: any) => {
				return item.name
			})
			showLocations.current = parseLocations.join(', ')
		}

		return result
	}, [
		getSeniorityLevel,
		getJobTypeLoc,
		getJobType,
		jobData,
		getNrHours,
		jobDetails,
		getSkills,
		isCreating,
		getLocations,
		getContractType,
		getJobTypeLocData,
	])

	const goToCompany = useCallback(
		() =>
			history.push(`/${i18n.language}/employers/${jobTemp.company.slug}`),
		[jobTemp, history, i18n.language],
	)
	// const onClickLearnMore = useCallback(
	// 	(pushTo: 'company' | 'team') => {
	// 		if (pushTo === 'team')
	// 			history.push(
	// 				`/${i18n.language}/employers/${jobTemp.company.slug}/teams/${jobTemp.team.slug}`,
	// 			)
	// 		else {
	// 			history.push(
	// 				`/${i18n.language}/employers/${jobTemp.company.slug}`,
	// 			)
	// 		}
	// 	},
	// 	[jobTemp, history, i18n.language],
	// )

	const handleApplyJob = useCallback(() => {
		if (jobTemp?.applyJobExternalUrl) {
			window.open(jobTemp.applyJobExternalUrl, '_blank')?.focus()
			return
		}
		if (
			!storeUserProfile ||
			(storeUserProfile.cvFilePath &&
				storeUserProfile.cvFilePath.length === 0)
		) {
			handleOpenApplyJobDialog()
		} else {
			if (
				!storeUserProfile ||
				(storeUserProfile.cvFilePath &&
					storeUserProfile.cvFilePath.length === 0)
			) {
				handleOpenApplyJobDialog()
			} else {
				// Todo Kundan1023
				// here only id is required in payload so commented isApplying
				applyJobMutation({
					variables: {
						// language: i18n.language,
						id: jobTemp.id,
						// isApplying: true,
					},
				})
				setCheckUserJobApplication(true)
				ReactGA.event({
					category: 'Job Apply',
					action: 'Job Successfully Applied',
				})
			}
		}
	}, [applyJobMutation, jobTemp, storeUserProfile, handleOpenApplyJobDialog])

	const ogTitle = `${jobTemp.title} @ ${
		jobTemp.team ? jobTemp.team.name + ' @ ' : ' @ '
	}${jobTemp.company.name}`

	const ogImg = !isCreating && jobData ? jobData[0].featuredImage : ''
	let ogDescription = ''
	if (!isCreating && jobTemp) {
		if (jobTemp.locations) {
			// the job has at least one location
			let locations = jobTemp?.locations?.map((item: any) => {
				return item.name
			})
			locations = locations.join(', ').replace(/,+$/, '') + ';'
			ogDescription += `${t('Locations')}: ${locations} `
		}
		if (jobTemp.skills) {
			// the job has at least one location
			let skills = jobTemp?.skills?.map((item: any) => {
				return item.title
			})
			skills = skills.join(', ').replace(/,+$/, '') + ';'
			ogDescription += `${t('Skills')}: ${skills} `
		}
		if (jobTemp.contractType) {
			ogDescription += `${t('Contract type')}: ${jobTemp.contractType}; `
		}
		if (jobTemp.jobType) {
			ogDescription += `${t('Kind of job')}: ${jobTemp.jobType}; `
		}
		if (jobTemp.jobTypeLoc) {
			ogDescription += `${jobTemp.jobTypeLoc}; `
		}
		if (jobTemp.seniority) {
			ogDescription += `${t('Seniority level')}: ${jobTemp.seniority}; `
		}
		if (jobTemp.description) {
			ogDescription += `${t('Description')}: ${truncateText(
				jobTemp.description,
				50,
			)} ` // TODO: add elipsis here
		}
		if (jobTemp.applyJobExternalUrl) {
			ogDescription += `${t('External link')}: ${
				jobTemp.applyJobExternalUrl
			}; `
		}
	}

	const onClickLearnMore = useCallback(
		(pushTo: 'company' | 'team') => {
			if (pushTo === 'team')
				window.open(
					`/${i18n.language}/employers/${jobTemp.company.slug}/teams/${jobTemp.team.slug}`,
					`_blank`,
				)
			else {
				window.open(
					`/${i18n.language}/employers/${jobTemp.company.slug}`,
					`_blank`,
				)
			}
		},
		[jobTemp, i18n.language],
	)

	const handleJob = useCallback(() => {
		const dbSkills = jobTemp?.skills?.map((item: any) => {
			return item.id
		})
		let dbSalary = null
		const { amountMin, amountMax, currency, rate } = jobTemp.salary
		if (amountMin && amountMax && currency && rate) {
			dbSalary = {
				amountMin: parseFloat(amountMin),
				amountMax: parseFloat(amountMax),
				currency: currency,
				rate: rate.toUpperCase(),
			}
		}

		let dbTeam = []
		if (jobTemp.team) {
			dbTeam.push(jobTemp.team.id)
		}
		let dbLocations = []
		if (jobTemp.locations) {
			dbLocations = jobTemp?.locations?.map((item: any) => {
				return item.id
			})
		}
		let dbContractType =
			jobDetails && jobDetails.job && jobDetails.job.contractType

		let dbSeniorityLevel =
			jobDetails && jobDetails.job && jobDetails.job.seniorityLevel

		// checks if arr with location types are both remote&on_site or it`s just one location
		let detailedJobTypeLoc = jobDetails?.job?.jobTypeLoc

		let dbJobTypeLoc
		if (detailedJobTypeLoc.length === 2) {
			dbJobTypeLoc = 'ALL'
		} else if (detailedJobTypeLoc.length === 1) {
			const [firstElement] = detailedJobTypeLoc
			dbJobTypeLoc = firstElement
		}

		let dbJobType = jobDetails?.job?.jobType
		let dbTeamLeader = jobDetails.job.teamLeader
			? jobDetails.job.teamLeader.value
			: ''

		// Todo kundan1023
		// please change salary type because i dont know how to test this feature
		const handleJobData = {
			id: jobTemp.id,
			company: jobTemp.company.id,
			title: jobTemp.title,
			expiryDate: jobTemp.expiryDate,
			description: jobTemp.description,
			salary: dbSalary as any,
			skills: dbSkills,
			benefits: jobTemp.benefits,
			contractType: dbContractType,
			featuredImage: jobTemp.featuredImage,
			videoUrl: jobTemp.featuredVideo,
			teamIds: dbTeam,
			locations: dbLocations,
			seniorityLevel: dbSeniorityLevel,
			locationType: dbJobTypeLoc,
			jobType: dbJobType,
			teamLeader: dbTeamLeader,
			applyJobExternalUrl: jobTemp.applyJobExternalUrl,
		}

		if (state.add) {
			postJobMutation({
				variables: {
					data: {
						...handleJobData,
						// language: i18n.language
					},
				},
			})
				.then((data: any) => {
					toast.success(t('The job has been saved successfully!'), {
						position: 'top-center',
						autoClose: 5000,
						hideProgressBar: false,
						closeOnClick: true,
						pauseOnHover: true,
						draggable: true,
					})
					storeDispatch({
						type: ECompanyActionTypes.setCompanySettingsTab,
						companySettingsTab: 'Jobs',
					})
					history.push({
						pathname: `/${i18n.language}/employers/${data.data.createJob.company.slug}/company-settings`,
					})
				})
				.catch(err => {
					console.log({ err })
					toast.error(
						t('The job could not be saved. Please try again!'),
						{
							position: 'top-center',
							autoClose: 5000,
							hideProgressBar: false,
							closeOnClick: true,
							pauseOnHover: true,
							draggable: true,
						},
					)
				})
		} else {
			editJobMutation({
				variables: {
					data: handleJobData,
				},
			})
				.then(() => {
					toast.success(t('The job has been saved successfully!'), {
						position: 'top-center',
						autoClose: 5000,
						hideProgressBar: false,
						closeOnClick: true,
						pauseOnHover: true,
						draggable: true,
					})
					history.push(`/${i18n.language}/jobs`)
				})
				.catch(() => {
					toast.error(
						t('The job could not be modified. Please try again!'),
						{
							position: 'top-center',
							autoClose: 5000,
							hideProgressBar: false,
							closeOnClick: true,
							pauseOnHover: true,
							draggable: true,
						},
					)
				})
		}
	}, [
		jobTemp,
		postJobMutation,
		history,
		i18n.language,
		jobDetails,
		t,
		editJobMutation,
		state,
		storeDispatch,
	])

	return (
		<>
			{loading ? (
				<Loading size={48} />
			) : (
				<>
					{!isCreating && (
						<Helmet>
							<meta property="og:title" content={ogTitle} />
							<meta
								property="og:url"
								content={window.location.href}
							/>
							<meta
								property="og:description"
								content={ogDescription.replace(
									/(<([^>]+)>)/gi,
									'',
								)}
							/>
							{ogImg && (
								<meta property="og:image" content={ogImg} />
							)}
						</Helmet>
					)}
					<Grid
						className={classes.jobProfileContainer}
						container
						//direction="column"
					>
						<Grid className={classes.jobProfileMainContent}>
							<Grid item>
								<Grid
									container
									className={classes.pageTitleSection}
								>
									<Hidden smUp>
										<Grid
											item
											xs={12}
											sm={6}
											className={classes.jobHeaderSection}
										>
											{!isCreating && (
												<div
													className={
														classes.buttonsContainer
													}
												>
													<Fab
														variant="extended"
														size="small"
														color="secondary"
														aria-label="Apply"
														className={
															classes.applyButton
														}
														onClick={handleApplyJob}
														disabled={
															checkUserJobApplication
														}
													>
														<span>
															{checkUserJobApplication
																? t('Applied')
																: t('Apply')}
														</span>
													</Fab>
												</div>
											)}
										</Grid>
									</Hidden>
									<Grid item xs={12} sm={6}>
										<div
											className={classes.companyBadgeWrap}
										>
											<Badge
												company={
													jobTemp && jobTemp.company
												}
												title={
													jobTemp &&
													jobTemp.company.name
												}
												type={EEntity.company}
												description={
													jobTemp &&
													jobTemp.company.industry &&
													jobTemp.company.industry.key
												}
												extraClasses={
													classes.companyBadge
												}
												extraAvatarClasses={
													classes.companyBadgeAvatar
												}
												onClick={goToCompany}
											/>
										</div>
									</Grid>
									<Hidden xsDown>
										<Grid
											item
											xs={12}
											sm={6}
											className={classes.jobHeaderSection}
										>
											{!isCreating && (
												<div
													className={
														classes.buttonsContainer
													}
												>
													<Fab
														variant="extended"
														size="small"
														color="secondary"
														aria-label="Apply"
														onClick={handleApplyJob}
														className={
															classes.applyButton
														}
														disabled={
															checkUserJobApplication
														}
													>
														<span>
															{checkUserJobApplication
																? t('Applied')
																: t('Apply')}
														</span>
													</Fab>
												</div>
											)}
										</Grid>
									</Hidden>
								</Grid>
								<Grid
									container
									className={classes.jobTitleSection}
								>
									<Grid item xs={12} sm={5}>
										<span className={classes.jobTitle}>
											{jobTemp && jobTemp.title}
										</span>
										<div className={classes.infoContainer}>
											<span
												className={
													classes.jobDescriptionItem
												}
											>
												{t('Salary Range')}:
												<span
													className={
														jobTemp.featuredImage
															? classes.salaryRange
															: ''
													}
												>
													{jobTemp?.salary
														?.amountMin &&
													jobTemp?.salary
														?.amountMax &&
													jobTemp?.salary?.currency &&
													jobTemp?.salary?.rate
														? `${
																jobTemp.salary
																	.amountMin
														  }-${
																jobTemp.salary
																	.amountMax
														  } ${
																jobTemp.salary
																	.currency &&
																jobTemp.salary.currency.toUpperCase()
														  } - ${
																jobTemp.salary.rate.substring(
																	0,
																	1,
																) +
																jobTemp.salary.rate
																	.toLowerCase()
																	.substring(
																		1,
																	)
														  }`
														: ' - '}
												</span>
											</span>
											<span
												className={
													classes.jobDescriptionItem
												}
											>
												{`${t('Contract Type')}:  ${
													jobTemp &&
													jobTemp.contractType &&
													jobTemp.contractType
												}`}
											</span>
											<span
												className={
													classes.jobDescriptionItem
												}
											>
												{`${t('Post Type')}:  ${
													jobTemp?.jobType
												}`}
											</span>
											<span
												className={
													classes.jobDescriptionItem
												}
											>
												{`${t('Location Type')}:  ${
													jobTemp?.jobTypeLoc
														? jobTemp?.jobTypeLoc
														: '-'
												}`}
											</span>
											<span
												className={
													classes.jobDescriptionItem
												}
											>
												{`${t('Seniority Level')}:  ${
													jobTemp?.seniority
												}`}
											</span>
											<span
												className={
													classes.jobDescriptionItem
												}
											>
												{`${t('Hours')}: ${
													jobTemp.nrHours
												}`}
											</span>
											<span
												className={
													classes.jobDescriptionItem
												}
											>
												{
													`${t('Location')} : ${
														jobTemp &&
														jobTemp.locations &&
														jobTemp.locations
															?.map(
																(item: any) =>
																	item.name,
															)
															.join(', ')
													}`
													//TODO: Add dynamic content here.
												}
											</span>
											{jobTemp?.applyJobExternalUrl ? (
												<span
													className={
														classes.jobDescriptionItem
													}
												>
													{`External URL:`}{' '}
													<a
														target="_blank"
														href={
															jobTemp.applyJobExternalUrl
														}
														rel="noopener noreferrer"
													>
														{
															jobTemp.applyJobExternalUrl
														}
													</a>
												</span>
											) : null}
										</div>
									</Grid>
									<Grid item xs={12} sm={7}>
										{jobTemp?.featuredImage && (
											<img
												className={classes.jobImage}
												// src={CompanyImage}
												src={
													jobTemp.featuredImage &&
													buildJobImage(
														jobTemp.id,
														jobTemp.featuredImage,
													)
												}
												alt={''}
											/>
										)}
										{jobTemp?.featuredVideo && (
											<div className={classes.video}>
												<ReactPlayer
													url={jobTemp?.featuredVideo}
													style={{
														flex: 1,
														overflow: 'hidden',
													}}
													className={classes.player}
													controls={true}
												/>
											</div>
										)}
									</Grid>
								</Grid>
							</Grid>
							<Grid item>
								<Divider />
								<div className={classes.labelText}>
									{t('Benefits')}
								</div>

								<JobEditor
									editable={false}
									textCategory={EJobTextCategory.benefits}
									persistentPreview={true}
									previewBenefits={jobTemp.benefits}
								/>
							</Grid>
							<Grid item>
								<Divider />
								<div className={classes.labelText}>
									{t('Skills required')}
								</div>
								<div>
									{jobTemp &&
										jobTemp?.skills &&
										jobTemp?.skills?.map((item: any) => {
											return (
												<Chip
													key={'skill' + item.id}
													className={classes.chip}
													label={item.title}
													size={'small'}
													clickable={false}
												/>
											)
										})}
								</div>
							</Grid>
							<Grid item>
								<Divider />
								<div className={classes.labelText}>
									{t('Job description')}
								</div>
								<JobEditor
									editable={false}
									textCategory={EJobTextCategory.description}
									persistentPreview={true}
									previewDescription={jobTemp.description}
								/>
							</Grid>

							<Grid className={classes.teamSectionWrap}>
								{/* <Grid item> */}
								{/* <Grid
										container={true}
										className={classes.aboutTeamSection}
									> */}
								<Grid item className={classes.teamSectionTitle}>
									<span>{t('About the')}</span>
									<span
										className={classes.teamSectionTitleMain}
									>
										{jobTemp?.company && !jobTemp.team
											? t('Company-1')
											: t('Team')}
									</span>
								</Grid>
								<Grid
									item
									xs={12}
									className={classes.teamHeaderRoot}
								>
									{jobTemp?.team && (
										<CardDescription
											team={jobTemp.team}
											onClickLearnMore={onClickLearnMore}
										/>
									)}

									{jobTemp?.company && !jobTemp.team && (
										<CardDescription
											company={jobTemp.company}
											onClickLearnMore={onClickLearnMore}
										/>
									)}
								</Grid>
								{/* </Grid> */}
								{/* </Grid> */}
							</Grid>
						</Grid>
					</Grid>
					{isCreating && (
						<div className={classes.postButton}>
							{!postJobLoading && (
								<div className={classes.saveContainer}>
									<div
										className={'saveContainerText'}
										onClick={handleEditJobDetails}
									>
										{t('Edit Job Details')}
									</div>
									<Chip
										label={t('Post job')}
										variant="outlined"
										classes={{
											root: classes.chipSaveButton,
											// clickable: classes.chipSaveButton,
										}}
										onClick={handleJob}
									/>
								</div>
							)}
							{postJobLoading && (
								<CircularProgress
									size={20}
									classes={{
										root: classes.loader,
									}}
								/>
							)}
						</div>
					)}
				</>
			)}
			<DialogJobApplicants
				open={openDialogUploadCvInfo}
				closeDialog={handleOpenApplyJobDialog}
				isUserLogged={!storeUserProfile ? false : true}
				handleLogIn={handleLogIn}
			/>
		</>
	)
}

const JobProfile = memo((props: any) => (
	<StoreValue
		keys={[
			'user.id|storeUserId',
			'storeDispatch',
			'user.profile|storeUserProfile',
		]}
	>
		<JobProfileView {...props} />
	</StoreValue>
))

export default JobProfile
